import { gql, useMutation } from '@apollo/client';
import { useToast } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { poll_voted } from '@/analytics';
import { logError } from '@/imports/logging/ClientLogger';

import { usePollOption_PollV2Fragment } from './graphql-types/usePollOption_PollV2Fragment';
import {
  usePollOptions_CreatePollResponse,
  usePollOptions_CreatePollResponseVariables,
} from './graphql-types/usePollOptions_CreatePollResponse';
import {
  usePollOptions_UpdatePollResponse,
  usePollOptions_UpdatePollResponseVariables,
} from './graphql-types/usePollOptions_UpdatePollResponse';

const usePollOption_PollResponseFragment = gql`
  fragment usePollOption_PollResponse on PollResponse {
    _id
    pollPost {
      _id
      title
      responseCount
      options {
        _id
        responseCount
        responses {
          _id
          user {
            _id
          }
        }
      }
      currentUserResponse {
        _id
        optionId
      }
      currentUserCanViewPollResults
    }
  }
`;

const UPDATE_POLL = gql`
  mutation usePollOptions_UpdatePollResponse($optionId: String!) {
    updatePollResponseV2(input: { optionId: $optionId }) {
      data {
        _id
        ...usePollOption_PollResponse
      }
    }
  }
  ${usePollOption_PollResponseFragment}
`;

const CREATE_POLL = gql`
  mutation usePollOptions_CreatePollResponse($optionId: String!) {
    createPollResponseV2(input: { optionId: $optionId }) {
      data {
        _id
        ...usePollOption_PollResponse
      }
    }
  }
  ${usePollOption_PollResponseFragment}
`;

export const usePollOption: {
  (poll: usePollOption_PollV2Fragment): {
    vote: (options: { optionId: string }) => Promise<void>;
  };
  pollFragment: DocumentNode;
} = (
  poll: usePollOption_PollV2Fragment
): {
  vote: (options: { optionId: string }) => Promise<void>;
} => {
  const toast = useToast();

  const [updateVote] = useMutation<
    usePollOptions_UpdatePollResponse,
    usePollOptions_UpdatePollResponseVariables
  >(UPDATE_POLL);
  const [createVote] = useMutation<
    usePollOptions_CreatePollResponse,
    usePollOptions_CreatePollResponseVariables
  >(CREATE_POLL);

  const vote = React.useCallback(
    async (options: { optionId: string }): Promise<void> => {
      const { optionId } = options;
      const selectedOption = poll.options?.find(
        (option) => option._id === optionId
      );
      const pollContext = poll as any;
      const commonVoteProperties = {
        entity_id: optionId,
        entity_type: 'poll_vote',
        parent_id: poll._id,
        parent_type: 'poll',
        poll_id: poll._id,
        poll_option_id: optionId,
        poll_option_text: selectedOption?.description,
        poll_option_count: poll.options?.length ?? 0,
        content_text: pollContext.title,
        location_posted_in: pollContext.organization
          ? 'organization'
          : pollContext.event
          ? 'event'
          : pollContext.group?.isGlobalSingleton
          ? 'home'
          : 'group',
        location_group_id:
          !pollContext.organization && !pollContext.event
            ? pollContext.group?._id
            : undefined,
        location_organization_id: pollContext.organization?._id,
        location_event_id: pollContext.event?._id,
      };

      try {
        if (!poll?.currentUserResponse?.optionId) {
          const result = await createVote({
            variables: {
              optionId,
            },
            optimisticResponse: {
              createPollResponseV2: {
                __typename: 'CreatePollResponseOutput_V2',
                data: {
                  __typename: 'PollResponse',
                  _id: 'temp-id-create-vote',
                  pollPost: {
                    ...poll,
                    __typename: 'PollV2',
                    _id: 'temp-poll-post-id-create-vote',
                    responseCount: poll.responseCount + 1,
                    currentUserResponse: {
                      __typename: 'PollResponse',
                      _id: 'temp-poll-response-id-create-vote',
                      optionId,
                    },
                    currentUserCanViewPollResults: true,
                  },
                },
              },
            },
          });

          if (result.data?.createPollResponseV2.data) {
            poll_voted({
              ...commonVoteProperties,
              entity_id: result.data.createPollResponseV2.data._id,
              metadata_outcome: 'created',
            });
          }
        } else {
          const result = await updateVote({
            variables: {
              optionId,
            },
            optimisticResponse: {
              updatePollResponseV2: {
                __typename: 'UpdatePollResponseOutput_V2',
                data: {
                  __typename: 'PollResponse',
                  _id: 'temp-id-update-vote',
                  pollPost: {
                    ...poll,
                    __typename: 'PollV2',
                    _id: 'temp-poll-post-id-update-vote',
                    currentUserResponse: {
                      __typename: 'PollResponse',
                      _id: 'temp-poll-response-id-update-vote',
                      optionId,
                    },
                    currentUserCanViewPollResults: false,
                  },
                },
              },
            },
          });

          if (result.data?.updatePollResponseV2.data) {
            poll_voted({
              ...commonVoteProperties,
              entity_id: result.data.updatePollResponseV2.data._id,
              metadata_outcome: 'updated',
            });
          }
        }
      } catch (err) {
        logError(err);
        toast({
          description: `Failed to update poll`,
          status: 'error',
        });
      }
    },
    [poll, createVote, updateVote, toast]
  );

  return {
    vote,
  };
};

usePollOption.pollFragment = gql`
  fragment usePollOption_PollV2Fragment on PollV2 {
    _id
    title
    responseCount
    group {
      _id
      isGlobalSingleton
    }
    organization {
      _id
    }
    event {
      _id
    }
    currentUserCanViewPollResults
    options {
      _id
      description
      responseCount
      order
      responses {
        _id
        user {
          _id
        }
      }
    }
    currentUserResponse {
      _id
      optionId
    }
  }
`;
